import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { CountryMeta } from "../models/CountryMeta";
import { CountryMetaList } from "../data/CountryMetaList";

interface PhoneInputWithCountrySelectListProps {
  // Basic Props
  value?: { countryCode: string; phone: string };
  onChange?: (value: { countryCode: string; phone: string }) => void;
  
  // UI Customization
  label?: string;
  placeholder?: string;
  countryPlaceholder?: string;
  
  // Behavior
  required?: boolean;
  disabled?: boolean;
  
  // Validation
  validationMessages?: {
    phoneRequired?: string;
    phoneInvalid?: string;
    phoneLengthInvalid?: string;
    countryRequired?: string;
  };
}

const defaultMessages = {
  label: "Phone Number",
  placeholder: "123-456-7890",
  countryPlaceholder: "Search country...",
  phoneRequired: "Phone number is required",
  phoneInvalid: "Invalid phone number format",
  phoneLengthInvalid: "Phone number must have {length} digits",
  countryRequired: "Country selection is required",
};

export const PhoneInputWithCountrySelectList: React.FC<PhoneInputWithCountrySelectListProps> = ({
  value = { countryCode: "", phone: "" },
  onChange,
  label,
  placeholder,
  countryPlaceholder,
  required = false,
  disabled = false,
  validationMessages = {},
}) => {
  // State
  const [selectedCountry, setSelectedCountry] = useState<CountryMeta | null>(null);
  const [phone, setPhone] = useState<string>(value.phone || "");
  const [errors, setErrors] = useState<{ countryCode?: string; phone?: string }>({});
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Countries list
  const countries = CountryMetaList;
  
  // Filter countries based on search query
  const filteredCountries = countries.filter(country => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase().trim();
    const countryName = country.label?.toLowerCase() || '';
    const countryCode = country.code?.toLowerCase() || '';
    const phoneCodeNumbers = country.code?.replace('+', '') || '';
    const countryValueCode = country.value?.toLowerCase() || '';
    
    return (
      countryName.includes(query) ||           // Search by name: "india"
      countryCode.includes(query) ||           // Search by full code: "+91"
      phoneCodeNumbers.includes(query) ||     // Search by numbers: "91"
      countryValueCode.includes(query)        // Search by ISO code: "in"
    );
  });
  
  // Initialize with India as default
  useEffect(() => {
    console.log('Countries loaded:', countries.length, 'items');
    console.log('First 3 countries:', countries.slice(0, 3));
    if (!selectedCountry && countries.length > 0) {
      const defaultCountry = countries.find(c => c.code === "+91") || countries[0];
      console.log('Setting default country:', defaultCountry?.label, defaultCountry?.code);
      setSelectedCountry(defaultCountry);
      if (onChange) {
        onChange({ countryCode: defaultCountry.code, phone });
      }
    }
  }, []);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearchQuery(""); // Clear search when closing
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  
  // Update internal state when value prop changes
  useEffect(() => {
    if (value.countryCode !== selectedCountry?.code) {
      const country = countries.find(c => c.code === value.countryCode);
      setSelectedCountry(country || null);
    }
    if (value.phone !== phone) {
      setPhone(value.phone);
    }
  }, [value]);

  // Phone number formatting function
  const formatPhoneNumber = (phone: string, countryCode: string): string => {
    const digitsOnly = phone.replace(/\D/g, "");
    const country = countries.find(c => c.code === countryCode);
    
    if (!country || !digitsOnly) return digitsOnly;
    
    // Different formatting patterns based on country
    switch (countryCode) {
      case "+1": // US/Canada: (123) 456-7890
        if (digitsOnly.length <= 3) return digitsOnly;
        if (digitsOnly.length <= 6) return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
        return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
      
      case "+44": // UK: 020 1234 5678
        if (digitsOnly.length <= 3) return digitsOnly;
        if (digitsOnly.length <= 7) return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3)}`;
        return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 7)} ${digitsOnly.slice(7, 11)}`;
      
      case "+91": // India: 98765 43210
        if (digitsOnly.length <= 5) return digitsOnly;
        return `${digitsOnly.slice(0, 5)} ${digitsOnly.slice(5, 10)}`;
      
      case "+86": // China: 138 0013 8000
        if (digitsOnly.length <= 3) return digitsOnly;
        if (digitsOnly.length <= 7) return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3)}`;
        return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 7)} ${digitsOnly.slice(7, 11)}`;
      
      case "+33": // France: 01 23 45 67 89
        if (digitsOnly.length <= 2) return digitsOnly;
        if (digitsOnly.length <= 4) return `${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2)}`;
        if (digitsOnly.length <= 6) return `${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4)}`;
        if (digitsOnly.length <= 8) return `${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4, 6)} ${digitsOnly.slice(6)}`;
        return `${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4, 6)} ${digitsOnly.slice(6, 8)} ${digitsOnly.slice(8, 10)}`;
      
      case "+49": // Germany: 030 12345678
        if (digitsOnly.length <= 3) return digitsOnly;
        return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 11)}`;
      
      case "+81": // Japan: 090-1234-5678
        if (digitsOnly.length <= 3) return digitsOnly;
        if (digitsOnly.length <= 7) return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
        return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 7)}-${digitsOnly.slice(7, 11)}`;
      
      case "+61": // Australia: 0412 345 678
        if (digitsOnly.length <= 4) return digitsOnly;
        if (digitsOnly.length <= 7) return `${digitsOnly.slice(0, 4)} ${digitsOnly.slice(4)}`;
        return `${digitsOnly.slice(0, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7, 10)}`;
      
      default:
        // Generic formatting: space every 3-4 digits
        if (digitsOnly.length <= 4) return digitsOnly;
        if (digitsOnly.length <= 7) return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3)}`;
        return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6)}`;
    }
  };

  // Enhanced filter for countries - search by name, code, and phone code numbers
  const filterCountries = (option: any, text: string) => {
    if (!text.trim()) return true;
    
    const query = text.toLowerCase().trim();
    const countryName = option.label?.toLowerCase() || '';
    const countryCode = option.code?.toLowerCase() || '';
    const phoneCodeNumbers = option.code?.replace('+', '') || ''; // Remove + for number search
    const countryValueCode = option.value?.toLowerCase() || ''; // ISO code like "IN" for India
    
    return (
      countryName.includes(query) ||           // Search by name: "india"
      countryCode.includes(query) ||           // Search by full code: "+91"
      phoneCodeNumbers.includes(query) ||     // Search by numbers: "91"
      countryValueCode.includes(query)        // Search by ISO code: "in"
    );
  };

  // Highlight matching text in search results
  const highlightMatch = (text: string, query: string): React.ReactNode => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="search-highlight">{part}</mark>
      ) : (
        part
      )
    );
  };

  // Validation
  const validate = (countryCode: string, phoneVal: string) => {
    const newErrors: { countryCode?: string; phone?: string } = {};
    const country = countries.find(c => c.code === countryCode);
    
    if (required && !phoneVal.trim()) {
      newErrors.phone = validationMessages.phoneRequired || defaultMessages.phoneRequired;
    } else if (phoneVal.trim()) {
      if (!country || !country.phoneLength) {
        newErrors.phone = validationMessages.phoneInvalid || defaultMessages.phoneInvalid;
      } else {
        const digitsOnly = phoneVal.replace(/\D/g, "");
        if (digitsOnly.length !== country.phoneLength) {
          let msg = validationMessages.phoneLengthInvalid;
          if (msg) {
            msg = msg.replace("{length}", String(country.phoneLength));
            newErrors.phone = msg;
          } else {
            newErrors.phone = `${defaultMessages.phoneLengthInvalid.replace("{length}", String(country.phoneLength))}`;
          }
        }
      }
    }
    
    if (required && phoneVal.trim() && !countryCode) {
      newErrors.countryCode = validationMessages.countryRequired || defaultMessages.countryRequired;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Event handlers
  const handleCountrySelect = (selections: CountryMeta[]) => {
    const country = selections[0];
    if (country) {
      setSelectedCountry(country);
      setIsDropdownOpen(false);
      setSearchQuery(""); // Clear search after selection
      
      // Format existing phone number for new country
      const formattedPhone = formatPhoneNumber(phone, country.code);
      setPhone(formattedPhone);
      
      if (onChange) {
        onChange({ countryCode: country.code, phone: formattedPhone });
      }
      
      validate(country.code, formattedPhone);
    }
  };
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => 
        prev < filteredCountries.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => 
        prev > 0 ? prev - 1 : filteredCountries.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredCountries[highlightedIndex]) {
        handleCountrySelect([filteredCountries[highlightedIndex]]);
      } else if (filteredCountries.length > 0) {
        handleCountrySelect([filteredCountries[0]]);
      }
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      setSearchQuery("");
      setHighlightedIndex(-1);
    }
  };
  
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawPhone = e.target.value;
    const countryCode = selectedCountry?.code || "";
    
    // Format the phone number based on selected country
    const formattedPhone = formatPhoneNumber(rawPhone, countryCode);
    setPhone(formattedPhone);
    
    if (onChange) {
      onChange({ countryCode, phone: formattedPhone });
    }
    
    validate(countryCode, formattedPhone);
  };
  
  const handlePhoneFocus = () => {
    setIsFocused(true);
  };
  
  const handlePhoneBlur = () => {
    setIsFocused(false);
    validate(selectedCountry?.code || "", phone);
  };

  return (
    <div className="modern-phone-input" ref={containerRef}>
      {/* Label */}
      {label && (
        <label className="phone-input-label">
          {label}
          {required && <span className="required-asterisk"> *</span>}
        </label>
      )}
      
      {/* Modern Phone Input Container */}
      <div className={clsx("phone-input-wrapper", {
        focused: isFocused || isDropdownOpen,
        error: !!(errors.phone || errors.countryCode),
        disabled: disabled,
      })}>
        
        {/* Country Selector Section */}
        <div className="country-selector-section">
          <div className="country-selector-container">
            {/* Clean Country Selector Button */}
            <button
              type="button"
              className={clsx("country-selector-button", {
                open: isDropdownOpen
              })}
              onClick={() => {
                console.log('Dropdown button clicked, current state:', isDropdownOpen);
                setIsDropdownOpen(!isDropdownOpen);
                if (!isDropdownOpen) {
                  setSearchQuery(""); // Clear search when opening
                }
              }}
              disabled={disabled}
            >
              {selectedCountry ? (
                <>
                  <img 
                    src={selectedCountry.flag || "https://flagcdn.com/24x18/us.png"} 
                    alt={`${selectedCountry.label} flag`}
                    className="country-flag"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "https://flagcdn.com/24x18/us.png";
                    }}
                  />
                  <span className="country-code">{selectedCountry.code}</span>
                </>
              ) : (
                <span className="country-code">Select</span>
              )}
              <svg className="dropdown-arrow" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Phone Number Input Section */}
        <div className="phone-input-section">
          <input
            type="tel"
            className="phone-number-input"
            placeholder={placeholder || defaultMessages.placeholder}
            value={phone}
            onChange={handlePhoneChange}
            onFocus={handlePhoneFocus}
            onBlur={handlePhoneBlur}
            disabled={disabled}
            required={required}
          />
        </div>
      </div>
      
      {/* Dropdown Menu - Outside the wrapper to avoid overflow issues */}
      {isDropdownOpen && (
        <div className="country-dropdown-menu">
          {/* Search Input */}
          <div className="country-search-container">
            <input
              ref={searchInputRef}
              type="text"
              className="country-search-input"
              placeholder="Search countries..."
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              autoComplete="off"
            />
          </div>
          
          {/* Countries List - Filtered countries */}
          <div className="countries-list">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country: CountryMeta) => (
                <div
                  key={country.value}
                  className={clsx("country-option", {
                    selected: selectedCountry?.value === country.value
                  })}
                  onClick={() => {
                    console.log('Country selected:', country.label, country.code);
                    handleCountrySelect([country]);
                  }}
                  onMouseDown={(e) => e.preventDefault()} // Prevent blur
                >
                  <img 
                    src={country.flag || `https://flagcdn.com/24x18/${country.value.toLowerCase()}.png`} 
                    alt={`${country.label} flag`}
                    className="country-flag"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://flagcdn.com/24x18/${country.value.toLowerCase()}.png`;
                    }}
                  />
                  <div className="country-details">
                    <span className="country-code">
                      ({highlightMatch(country.code, searchQuery)})
                    </span>
                    <span className="country-name">
                      {highlightMatch(country.label, searchQuery)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">
                No countries found for "{searchQuery}"
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Error Messages */}
      {(errors.countryCode || errors.phone) && (
        <div className="error-message">
          {errors.countryCode || errors.phone}
        </div>
      )}
    </div>
  );
};